<template>
  <!-- Loading Overlay -->
  <div
    v-if="initialLoading"
    class="fixed inset-0 bg-white dark:bg-gray-900 z-50 flex items-center justify-center"
  >
    <div class="text-center">
      <i class="pi pi-spin pi-spinner text-5xl text-teal-600 mb-4"></i>
      <p class="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
    </div>
  </div>

  <div v-else class="w-full mt-6">
    <!-- Active Event Banners -->
    <router-link
      v-for="event in liveEvents"
      :key="event.id"
      :to="`/events/live/${event.id}`"
      class="block mb-6 mx-6 mt-6 rounded-lg overflow-hidden hover:opacity-90 transition-opacity first:mt-6"
    >
      <div class="flex items-center gap-6 p-6 text-white" style="background-color: #006666">
        <div class="flex-1 min-w-0">
          <div class="text-2xl font-bold mb-2">{{ event.name }}</div>
          <div v-if="event.location" class="text-lg mb-2 flex items-center gap-2">
            <i class="pi pi-map-marker text-lg"></i>
            <span>{{ event.location }}</span>
          </div>
          <div v-if="event.description" class="text-base opacity-90 line-clamp-2">
            {{ event.description }}
          </div>
        </div>
        <div class="flex-shrink-0">
          <button class="btn btn-secondary flex items-center gap-2">
            Go to the event
            <i class="pi pi-chevron-right text-xs"></i>
          </button>
        </div>
      </div>
    </router-link>

    <!-- Main Content Cards (more important for mobile) -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 mx-6">
      <!-- Next Upcoming Event Card -->
      <router-link
        to="/events"
        class="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-teal-500 dark:hover:border-teal-400 transition-colors cursor-pointer block"
      >
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <h2 class="text-xl text-gray-900 dark:text-white m-0">Next Upcoming Event</h2>
            <i class="pi pi-calendar-plus text-3xl text-gray-500 dark:text-gray-300"></i>
          </div>
        </div>
        <div class="p-6">
          <div
            v-if="loadingEvent"
            class="flex items-center justify-center"
            style="min-height: 200px"
          >
            <i class="pi pi-spin pi-spinner text-3xl text-gray-500 dark:text-gray-300"></i>
          </div>
          <div v-else-if="nextEvent" class="flex flex-col gap-4">
            <div>
              <div class="text-md font-bold text-teal-700 dark:text-teal-300">
                {{ nextEvent.name }}
              </div>
              <div class="text-sm flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-3">
                <i class="pi pi-map-marker"></i>
                <span v-if="nextEvent.location">{{ nextEvent.location }}</span>
                <span v-else>Location TBD</span>
              </div>
            </div>

            <div class="flex flex-col gap-3">
              <div class="flex items-center gap-3">
                <div
                  class="flex items-center justify-center bg-white dark:bg-gray-900 border-2 border-blue-200 dark:border-blue-800 rounded-lg"
                  style="width: 2.5rem; height: 2.5rem"
                >
                  <i class="pi pi-calendar text-blue-500 dark:text-blue-300"></i>
                </div>
                <div>
                  <div class="text-gray-900 dark:text-white font-medium">Event Date</div>
                  <div class="text-gray-500 dark:text-gray-400">
                    {{ formatEventStartTime(nextEvent) }}
                  </div>
                </div>
              </div>

              <div v-if="nextEvent.votingStartTime" class="flex items-center gap-3">
                <div
                  class="flex items-center justify-center bg-white dark:bg-gray-900 border-2 border-green-200 dark:border-green-800 rounded-lg"
                  style="width: 2.5rem; height: 2.5rem"
                >
                  <i class="pi pi-clock text-green-500 dark:text-green-300"></i>
                </div>
                <div>
                  <div class="text-gray-900 dark:text-white font-medium">Voting Starts</div>
                  <div class="text-gray-500 dark:text-gray-400">
                    {{ formatDateTime(nextEvent.votingStartTime) }}
                  </div>
                </div>
              </div>

              <div v-if="nextEvent.votingEndTime" class="flex items-center gap-3">
                <div
                  class="flex items-center justify-center bg-white dark:bg-gray-900 border-2 border-orange-200 dark:border-orange-800 rounded-lg"
                  style="width: 2.5rem; height: 2.5rem"
                >
                  <i class="pi pi-stop-circle text-orange-500 dark:text-orange-300"></i>
                </div>
                <div>
                  <div class="text-gray-900 dark:text-white font-medium">Voting Ends</div>
                  <div class="text-gray-500 dark:text-gray-400">
                    {{ formatDateTime(nextEvent.votingEndTime) }}
                  </div>
                </div>
              </div>

              <div v-if="nextEvent.organizerName" class="flex items-center gap-3">
                <div
                  class="flex items-center justify-center bg-white dark:bg-gray-900 border-2 border-purple-200 dark:border-purple-800 rounded-lg"
                  style="width: 2.5rem; height: 2.5rem"
                >
                  <i class="pi pi-user text-purple-500 dark:text-purple-300"></i>
                </div>
                <div>
                  <div class="text-gray-900 dark:text-white font-medium">Organizer</div>
                  <div class="text-gray-500 dark:text-gray-400">{{ nextEvent.organizerName }}</div>
                </div>
              </div>
            </div>

            <div
              v-if="nextEvent.description"
              class="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700"
            >
              <div class="text-gray-500 dark:text-gray-400 text-sm">
                {{ nextEvent.description }}
              </div>
            </div>
          </div>
          <div
            v-else-if="eventError"
            class="flex items-center justify-center text-red-500"
            style="min-height: 200px"
          >
            <div class="text-center">
              <i class="pi pi-exclamation-circle text-4xl mb-3"></i>
              <div>{{ eventError }}</div>
            </div>
          </div>
          <div
            v-else
            class="flex items-center justify-center text-gray-500 dark:text-gray-400"
            style="min-height: 200px"
          >
            <div class="text-center">
              <i class="pi pi-calendar-times text-4xl mb-3"></i>
              <div>No upcoming events scheduled</div>
            </div>
          </div>
        </div>
      </router-link>

      <!-- Disabled Songs Statistics Card -->
      <router-link
        to="/songs"
        class="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-teal-500 dark:hover:border-teal-400 transition-colors cursor-pointer block"
      >
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <h2 class="text-xl text-gray-900 dark:text-white m-0">Song Status</h2>
            <i class="pi pi-book text-3xl text-gray-500 dark:text-gray-300"></i>
          </div>
        </div>
        <div class="p-6">
          <div
            v-if="loadingSongs"
            class="flex items-center justify-center"
            style="min-height: 200px"
          >
            <i class="pi pi-spin pi-spinner text-3xl text-gray-500 dark:text-gray-300"></i>
          </div>
          <div v-else class="flex flex-col gap-4">
            <!-- Active Songs -->
            <div
              class="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/30 rounded-lg"
            >
              <div class="flex items-center gap-3">
                <div
                  class="flex items-center justify-center bg-green-500 dark:bg-green-600 rounded-lg"
                  style="width: 3rem; height: 3rem"
                >
                  <i class="pi pi-check-circle text-2xl text-white"></i>
                </div>
                <div>
                  <div class="text-gray-900 dark:text-white font-bold text-xl">
                    {{ songStats.activeSongs || 0 }}
                  </div>
                  <div class="text-gray-600 dark:text-gray-300">Active Songs</div>
                </div>
              </div>
              <div class="text-right">
                <div class="text-2xl font-bold text-green-600 dark:text-green-300">
                  {{
                    songStats.totalSongs > 0
                      ? Math.round((songStats.activeSongs / songStats.totalSongs) * 100)
                      : 0
                  }}%
                </div>
                <div class="text-gray-500 dark:text-gray-400 text-sm">of total</div>
              </div>
            </div>

            <!-- Disabled Songs -->
            <div
              class="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/30 rounded-lg"
            >
              <div class="flex items-center gap-3">
                <div
                  class="flex items-center justify-center bg-red-500 dark:bg-red-600 rounded-lg"
                  style="width: 3rem; height: 3rem"
                >
                  <i class="pi pi-ban text-2xl text-white"></i>
                </div>
                <div>
                  <div class="text-gray-900 dark:text-white font-bold text-xl">
                    {{ songStats.disabledSongs || 0 }}
                  </div>
                  <div class="text-gray-600 dark:text-gray-300">Disabled Songs</div>
                </div>
              </div>
              <div class="text-right">
                <div class="text-2xl font-bold text-red-600 dark:text-red-300">
                  {{
                    songStats.totalSongs > 0
                      ? Math.round((songStats.disabledSongs / songStats.totalSongs) * 100)
                      : 0
                  }}%
                </div>
                <div class="text-gray-500 dark:text-gray-400 text-sm">of total</div>
              </div>
            </div>

            <!-- Total Songs -->
            <div
              class="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700"
            >
              <div class="text-gray-600 dark:text-gray-300 font-medium">Total Songs in Library</div>
              <div class="text-2xl font-bold text-gray-900 dark:text-white">
                {{ songStats.totalSongs || 0 }}
              </div>
            </div>

            <!-- Warning if many songs are disabled -->
            <div
              v-if="
                songStats.disabledSongs > 0 && songStats.disabledSongs / songStats.totalSongs > 0.1
              "
              class="p-3 bg-orange-50 dark:bg-orange-900/30 rounded-lg border-l-4 border-orange-500 dark:border-orange-600"
            >
              <div class="flex items-start gap-2">
                <i class="pi pi-info-circle text-orange-500 dark:text-orange-300 mt-1"></i>
                <div class="flex-1">
                  <div class="text-gray-900 dark:text-white font-medium mb-1">
                    High Number of Disabled Songs
                  </div>
                  <div class="text-gray-600 dark:text-gray-300 text-sm">
                    {{ songStats.disabledSongs }} songs ({{
                      Math.round((songStats.disabledSongs / songStats.totalSongs) * 100)
                    }}%) are currently disabled and unavailable for voting.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </router-link>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mx-6 mb-6">
      <!-- Total Events Card -->
      <div
        class="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
      >
        <div class="flex justify-between items-start mb-1">
          <span class="text-gray-600 dark:text-gray-300 font-medium text-sm">Events</span>
          <div
            class="flex items-center justify-center bg-blue-100 dark:bg-blue-900/40 rounded-lg"
            style="width: 1.75rem; height: 1.75rem"
          >
            <i class="pi pi-calendar text-lg text-blue-500 dark:text-blue-300"></i>
          </div>
        </div>
        <div class="text-2xl text-gray-900 dark:text-white mb-1">{{ stats.totalEvents || 0 }}</div>
        <span class="text-xs text-green-600 dark:text-green-300">All time</span>
      </div>

      <!-- Total Songs Card -->
      <div
        class="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
      >
        <div class="flex justify-between items-start mb-1">
          <span class="text-gray-600 dark:text-gray-300 font-medium text-sm">Songs</span>
          <div
            class="flex items-center justify-center bg-green-100 dark:bg-green-900/40 rounded-lg"
            style="width: 1.75rem; height: 1.75rem"
          >
            <i class="pi pi-book text-lg text-green-500 dark:text-green-300"></i>
          </div>
        </div>
        <div class="text-2xl text-gray-900 dark:text-white mb-1">{{ stats.totalSongs || 0 }}</div>
        <span class="text-xs text-green-600 dark:text-green-300"
          >{{ stats.activeSongs || 0 }} active</span
        >
      </div>

      <!-- Total Users Card -->
      <div
        class="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
      >
        <div class="flex justify-between items-start mb-1">
          <span class="text-gray-600 dark:text-gray-300 font-medium text-sm">Users</span>
          <div
            class="flex items-center justify-center bg-cyan-100 dark:bg-cyan-900/40 rounded-lg"
            style="width: 1.75rem; height: 1.75rem"
          >
            <i class="pi pi-users text-lg text-cyan-600 dark:text-cyan-300"></i>
          </div>
        </div>
        <div class="text-2xl text-gray-900 dark:text-white mb-1">{{ stats.totalUsers || 0 }}</div>
        <span class="text-xs text-green-600 dark:text-green-300">Registered</span>
      </div>

      <!-- Total Votes Card -->
      <div
        class="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
      >
        <div class="flex justify-between items-start mb-1">
          <span class="text-gray-600 dark:text-gray-300 font-medium text-sm">Votes</span>
          <div
            class="flex items-center justify-center bg-orange-100 dark:bg-orange-900/40 rounded-lg"
            style="width: 1.75rem; height: 1.75rem"
          >
            <i class="pi pi-heart text-lg text-orange-500 dark:text-orange-300"></i>
          </div>
        </div>
        <div class="text-2xl text-gray-900 dark:text-white mb-1">{{ stats.totalVotes || 0 }}</div>
        <span class="text-xs text-green-600 dark:text-green-300">All time</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import api from "../../services/api";
import { useAuthStore } from "../../stores/auth";
import { getLiveEvents } from "../../utils/eventStatus";

export default {
  name: "AdminDashboard",
  setup() {
    const authStore = useAuthStore();

    const stats = ref({
      totalEvents: 0,
      totalSongs: 0,
      totalUsers: 0,
      totalVotes: 0,
      activeSongs: 0
    });

    const nextEvent = ref(null);
    const liveEvents = ref([]);
    const loadingEvent = ref(false);
    const eventError = ref("");
    const songStats = ref({
      totalSongs: 0,
      activeSongs: 0,
      disabledSongs: 0
    });
    const loadingSongs = ref(false);
    const initialLoading = ref(true);

    const fetchStats = async () => {
      try {
        // Check if token exists before making request
        const token = localStorage.getItem("token");
        if (!token) {
          console.error("No authentication token found");
          return;
        }

        const response = await api.get("/api/stats/overall");
        if (response.data.success) {
          stats.value = {
            ...response.data.data,
            activeSongs: songStats.value.activeSongs
          };
        }
      } catch (error) {
        console.error("Failed to fetch stats:", error);
        console.error("Error response:", error.response?.data);
        // If it's an auth error, the interceptor should handle it
        if (error.response?.status === 401) {
          console.error("Authentication failed - token may be invalid or expired");
        }
      }
    };

    const fetchNextEvent = async () => {
      try {
        loadingEvent.value = true;
        const response = await api.get("/api/events");
        if (response.data.success) {
          const events = response.data.data.events || [];
          const now = new Date();

          const live = getLiveEvents(events, now);
          liveEvents.value = live;

          const liveIds = new Set(live.map((event) => event.id));

          // Filter out disabled events and find the next upcoming one
          const upcomingEvents = events
            .filter((event) => !event.isDisabled && event.eventStartTime && !liveIds.has(event.id))
            .map((event) => {
              let eventDate = null;
              if (event.eventStartTime) {
                eventDate = new Date(event.eventStartTime);
              }
              return { ...event, eventDate };
            })
            .filter((event) => event.eventDate && event.eventDate >= now)
            .sort((a, b) => a.eventDate - b.eventDate);

          nextEvent.value = upcomingEvents.length > 0 ? upcomingEvents[0] : null;
          eventError.value = "";
        } else {
          eventError.value = response.data.message || "Failed to fetch events";
        }
      } catch (error) {
        console.error("Failed to fetch events:", error);
        eventError.value =
          error.response?.data?.message || error.message || "Failed to fetch events";
      } finally {
        loadingEvent.value = false;
      }
    };

    const fetchSongStats = async () => {
      try {
        loadingSongs.value = true;
        const response = await api.get("/api/songs");
        if (response.data.success) {
          const songs = response.data.data.songs || [];
          const totalSongs = songs.length;
          const disabledSongs = songs.filter((song) => song.isDisabled).length;
          const activeSongs = totalSongs - disabledSongs;

          songStats.value = {
            totalSongs,
            activeSongs,
            disabledSongs
          };

          // Update stats with active songs count
          stats.value.activeSongs = activeSongs;
          stats.value.totalSongs = totalSongs;
        }
      } catch (error) {
        console.error("Failed to fetch songs:", error);
      } finally {
        loadingSongs.value = false;
      }
    };

    // Format event start time
    const formatEventStartTime = (event) => {
      if (event.eventStartTime) {
        const date = new Date(event.eventStartTime);
        return date.toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit"
        });
      }
      return "TBD";
    };

    const formatDateTime = (dateTimeString) => {
      if (!dateTimeString) return "TBD";
      const d = new Date(dateTimeString);
      return d.toLocaleString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit"
      });
    };

    onMounted(async () => {
      // Ensure user is authenticated before fetching data
      if (!authStore.isAuthenticated) {
        console.error("User not authenticated, skipping data fetch");
        initialLoading.value = false;
        return;
      }

      // Ensure token is available
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found in localStorage");
        initialLoading.value = false;
        return;
      }

      try {
        await Promise.all([fetchStats(), fetchNextEvent(), fetchSongStats()]);
      } finally {
        initialLoading.value = false;
      }
    });

    return {
      stats,
      nextEvent,
      liveEvents,
      loadingEvent,
      eventError,
      songStats,
      loadingSongs,
      initialLoading,
      formatEventStartTime,
      formatDateTime
    };
  }
};
</script>

<style scoped>
/* Make icons appear as outline/stroke style */
.icon-outline i {
  -webkit-text-stroke: 1.5px currentColor;
  -webkit-text-fill-color: transparent;
}
</style>
